<template>
  <section>
    <Tabs>
      <TabsHeader>
        <Tab
          v-for="(tab, index) in displayTabs"
          :key="tab"
          :index="index"
          :value="selectedTabIndex"
          :closable="false"
          @tabClick="(index) => changeTab(index)"
          >{{ tab }}<sup v-if="getTabDirtyStatus(tab)">*</sup>
        </Tab>
      </TabsHeader>
      <TabsBody>
        <TabPanelItem>
          <keep-alive exclude="HistoryTab,WhereUsedTab,OverviewTab">
            <component
              :is="currentTabComponent"
              :client-id="currentClientId"
              :attribute-id="currentAttributeId"
              :code-meta-informations-id="currentAttributeId"
              :read-only="readOnly"
              :load-data="loadData"
              :server-side-datasource="getHistoryData"
              :history-data="attributesStore.historyData"
              :get-where-used-data="getWhereUsedData"
              :where-used-data="attributesStore.whereUsedData"
              :code-type="'Attribute'"
              :name="name"
              :tab-name="tabName"
              :use-systems="true"
              :messages="getOverviewTabMessages"
              :code-meta-information-data="getCodeMetaData"
              :check-duplicate-short-name="checkDuplicateShortName"
              ref="componentRef"
              v-on="{ ...dynamicEventListner() }"
            />
          </keep-alive>
        </TabPanelItem>
      </TabsBody>
    </Tabs>
    <Modal
      :show="displaySaveModal"
      body-class="atb-border-none"
      footer-class="atb-flex atb-flex-row atb-justify-end atb-gap-8"
      @onModalClose="closeSaveModal"
    >
      <template #header> {{ $t('saveAttributeTitle') }} </template>
      <template #body>
        <div class="atb-pb-2">{{ $t('saveRevisionTitle') }}</div>
        <div class="atb-flex atb-flex-col atb-gap-4">
          <Textarea
            id="attributesDetailsCodeRevisionCommentTextboxModal"
            v-model="comment"
            field-control-class="atb-h-24"
          />
        </div>
      </template>
      <template #footer>
        <Button
          id="attributesDetailsCodeRevisionCancelButtonModal"
          motif="basic"
          @click="closeSaveModal"
        >
          {{ $t('cancel') }}
        </Button>
        <Button
          id="attributesDetailsCodeRevisionSaveButtonModal"
          @click="displayConfirmationModal = true"
          :disabled="comment.trim() === ''"
        >
          {{ $t('save') }}
        </Button>
      </template>
    </Modal>
    <Modal
      :show="displayConfirmationModal"
      body-class="atb-border-none"
      footer-class="atb-flex atb-flex-row atb-justify-end atb-gap-8"
      @onModalClose="closeConfirmationModal"
    >
      <template
        #header
        class="atb-p-4"
      >
        {{ $t('areYouSure') }}
      </template>
      <template #body>
        <div class="atb-flex atb-flex-col atb-gap-4">
          {{ $t('checkingCodeSave') }}
        </div>
      </template>
      <template #footer>
        <Button
          id="attributesDetailsCodeRevisionConfirmationCancelButtonModal"
          motif="basic"
          @click="closeConfirmationModal"
        >
          {{ $t('cancel') }}
        </Button>
        <Button
          id="attributesDetailsCodeRevisionConfirmationSaveButtonModal"
          @click="saveConfirmationModal"
        >
          {{ $t('save') }}
        </Button>
      </template>
    </Modal>
  </section>
</template>

<script lang="ts" setup>
  import { useAttributesStore } from '@/app/features/ams/attributes/attributes-store';
  import CodeTab from '@/app/features/ams/shared/details-tab/CodeTab.vue';
  import HistoryTab from '@/app/features/ams/shared/details-tab/HistoryTab.vue';
  import OverviewTab from '@/app/features/ams/shared/details-tab/OverviewTab.vue';
  import ValidateTab from '@/app/features/ams/shared/details-tab/ValidateTab.vue';
  import WhereUsedTab from '@/app/features/ams/shared/details-tab/WhereUsedTab.vue';
  import { useSystemsStore } from '@/app/features/ams/systems/systems-store';
  import { CodeType } from '@/app/shared/enums/code-type.enum';
  import { Motif } from '@/app/shared/enums/motif-enum';
  import { useAttributeLocker } from '@/app/shared/hooks/use-attribute-locker';
  import { useAlertStore } from '@/app/shared/stores/alert-store';
  import { useAuthStore } from '@/app/shared/stores/auth-store';
  import { AtbError } from '@/app/shared/types/errors/error.interface';
  import { AddTabEvent } from '@/app/shared/types/events/add-tab-event.interface';
  import { SaveAttributeCodeEvent } from '@/app/shared/types/events/attributes-event.interface';
  import { CodeMetaInformationDataEvent } from '@/app/shared/types/events/code-meta-information-data-event.interface';
  import { OverviewDataEvent } from '@/app/shared/types/events/overview-data-event.interface';
  import { ValidateFilterCodeEvent } from '@/app/shared/types/events/validate-tab-event.interface';
  import { Messages } from '@/app/shared/types/messages.interface';
  import { VersionControl } from '@/app/shared/types/version-control.interface';
  import { handleServerErrors } from '@/app/shared/utils/cmi-errors-handler.util';
  import { historyQueryParams } from '@/app/shared/utils/query-params-formatter.util';
  import Button from '@atb-ui/eds-vue/src/components/Button.vue';
  import Modal from '@atb-ui/eds-vue/src/components/Modal.vue';
  import Tab from '@atb-ui/eds-vue/src/components/Tabs/Tab.vue';
  import TabPanelItem from '@atb-ui/eds-vue/src/components/Tabs/TabPanelItem.vue';
  import Tabs from '@atb-ui/eds-vue/src/components/Tabs/Tabs.vue';
  import TabsBody from '@atb-ui/eds-vue/src/components/Tabs/TabsBody.vue';
  import TabsHeader from '@atb-ui/eds-vue/src/components/Tabs/TabsHeader.vue';
  import Textarea from '@atb-ui/eds-vue/src/components/Textarea.vue';
  import { FeatureTabs } from '@/app/shared/enums/tab-names.enum';
  import { useAbility } from '@casl/vue';
  import { IGetRowsParams } from 'ag-grid-community';
  import {
    computed,
    onUnmounted,
    ref,
    Ref,
    shallowRef,
    toRefs,
    watch,
  } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRouter } from 'vue-router';

  interface AttributesDetailsEvent {
    (_event: 'refreshAttributesAfterDelete'): void;
    (_event: 'createNewTab', _params: AddTabEvent): void;
  }
  interface AttributesDetailsProps {
    currentClientId: number;
    currentAttributeId: number;
    tabName: string;
  }
  const emit = defineEmits<AttributesDetailsEvent>();
  const props = defineProps<AttributesDetailsProps>();
  const { t } = useI18n();
  const router = useRouter();
  const attributesStore = useAttributesStore();
  const alertStore = useAlertStore();
  const authStore = useAuthStore();
  const systemsStore = useSystemsStore();
  const { can } = useAbility();
  const { currentClientId, currentAttributeId, tabName } = toRefs(props);
  const lockDurationSeconds = attributesStore.$state.lockDurationSeconds;

  const selectedTabIndex = ref(0);
  const tabs = ref([
    t('overview'),
    t('code'),
    t('validate'),
    t('history'),
    t('whereUsed'),
  ]);
  // TODO: Create an interface for this attribute or use an existing one
  const currentTabComponent: Ref<any> = shallowRef(OverviewTab);
  const componentRef = ref();
  const comment = ref('');
  const displaySaveModal = ref(false);
  const displayConfirmationModal = ref(false);
  const overviewTabMessages = ref({} as Messages);
  let CodeMetaInformationDataRef = ref({} as CodeMetaInformationDataEvent);

  const loadData = computed(() => {
    const { overviewData } = attributesStore.getAttributeById(
      currentAttributeId.value
    );
    return {
      code: overviewData?.versionControls[0]?.code || '',
      codeType: overviewData?.codeType || '',
      shortName: overviewData?.shortName || '',
      longName: overviewData?.longName || '',
      description: overviewData?.description || '',
      initializationValue: overviewData?.initializationValue || '',
      defaultValue: overviewData?.defaultValue || '',
      format: overviewData?.format || '',
    };
  });

  const getCodeMetaData = computed(() => {
    const { overviewData, isCopied } = attributesStore.getAttributeById(
      currentAttributeId.value
    );
    CodeMetaInformationDataRef.value.OverviewDataEvent = overviewData;
    CodeMetaInformationDataRef.value.isDirty = getTabDirtyStatus(tabName.value);
    CodeMetaInformationDataRef.value.isNew = getNewStatus.value;
    CodeMetaInformationDataRef.value.isCopied = isCopied.isCopy;
    return CodeMetaInformationDataRef;
  });

  const getOverviewTabMessages = computed(() => {
    overviewTabMessages.value.checkingDeletion = t('checkingAttributeDeletion');
    overviewTabMessages.value.checkingSave = t('checkingAttributeSave');
    overviewTabMessages.value.nameInvalid = t('attributesNameInvalid');
    return overviewTabMessages.value;
  });

  const displayTabs = computed(() => {
    return getNewStatus.value ? tabs.value.slice(0, 1) : tabs.value;
  });

  const name = computed(() => {
    return attributesStore.getAttributeById(currentAttributeId.value)
      .attributeName;
  });
  // TODO: Create an interface for this attribute or use an existing one
  const getTabDirtyStatus = (dirtyTabName: any) => {
    const isTabDirty = attributesStore.getTabDirtyStatus(
      currentAttributeId.value,
      dirtyTabName
    );
    return typeof isTabDirty !== 'undefined' ? isTabDirty : false;
  };

  const getNewStatus = computed(() => {
    return attributesStore.getAttributeById(currentAttributeId.value)?.isNew;
  });

  const readOnly = computed(() => {
    const attribute = attributesStore.getAttributeById(
      Number(currentAttributeId.value)
    );
    const versionControl: VersionControl | undefined =
      attribute?.overviewData.versionControls
        .filter((vc: VersionControl) => vc.dataSource == null)
        .shift();
    const isAdmin = can('WRITE', 'ALL');
    const isLockedByCurrentUser =
      versionControl?.lockedByUsername?.toLowerCase() ===
      authStore.username.split('@').shift()?.toLowerCase();
    const isLockedBySomeone =
      versionControl?.lockedByUserId ?? versionControl?.autoLockedByUserId;
    if (isAdmin || isLockedByCurrentUser) {
      return false;
    } else {
      return isLockedBySomeone;
    }
  });

  //todo: check if lockDurationSeconds needs to updated with every lock request
  const { startSubscription, endSubscription } = useAttributeLocker({
    attributeId: Number(currentAttributeId.value),
    clientId: currentClientId.value,
    firstPollingTimeInSeconds: lockDurationSeconds,
  });

  watch(
    () => getNewStatus.value,
    (newValue: boolean) => {
      if (newValue === false) {
        currentTabComponent.value = CodeTab;
        selectedTabIndex.value = 1;
        startSubscription();
      }
    },
    { immediate: true }
  );

  onUnmounted(() => {
    endSubscription();
  });

  const changeTab = async (index: number) => {
    selectedTabIndex.value = index;
    switch (index) {
      case FeatureTabs.OVERVIEW_TAB:
        await systemsStore.fetchAllSystemsByClient(currentClientId.value);
        currentTabComponent.value = OverviewTab;
        break;
      case FeatureTabs.CODE_TAB:
        currentTabComponent.value = CodeTab;
        break;
      case FeatureTabs.VALIDATE_TAB:
        currentTabComponent.value = ValidateTab;
        break;
      case FeatureTabs.HISTORY_TAB:
        currentTabComponent.value = HistoryTab;
        break;
      case FeatureTabs.WHERE_USED_TAB:
        currentTabComponent.value = WhereUsedTab;
        break;
      default:
        break;
    }
  };

  const deleteAttributeFromOverview = async () => {
    try {
      await attributesStore.deleteAttributes(currentClientId.value, [
        currentAttributeId.value,
      ]);
      attributesStore.deleteAttribute(currentAttributeId.value);
      emit('refreshAttributesAfterDelete');
      alertStore.showNotification(
        t('attributeDeleted'),
        'Attributes',
        Motif.SUCCESS
      );
    } catch (e) {
      const errorResponse = e as AtbError;
      alertStore.showNotification(
        handleServerErrors(t, errorResponse?.message),
        'Attributes',
        Motif.ERROR
      );
    }
  };

  const createNewTab = async (addTab: AddTabEvent) => {
    if (addTab.codeType?.toUpperCase() === CodeType.ATTRIBUTE) {
      try {
        await attributesStore.lockAttribute(
          currentClientId.value,
          Number(addTab.id),
          true
        );
        attributesStore.updateAutoLockStatusForTab(Number(addTab.id), true);
      } catch (error) {
        const errorResponse = error as AtbError;
        alertStore.showNotification(
          errorResponse.message,
          'Attributes',
          Motif.ERROR
        );
      }
      emit('createNewTab', {
        id: addTab.id,
        name: addTab.name,
        isNew: addTab.isNew,
        clientId: currentClientId.value,
        dirtyTab: addTab.dirtyTab,
      });
    } else {
      router.push({
        name: 'filters',
        params: {
          filterId: addTab.id,
          filterName: addTab.name,
        },
      });
    }
  };

  const saveOverviewData = async (attributeOverviewData: OverviewDataEvent) => {
    try {
      if (attributeOverviewData.id) {
        await attributesStore.updateAttribute(
          currentClientId.value,
          currentAttributeId.value,
          attributeOverviewData
        );
        setDirtyField(t('overview'), false);
        displaySuccessMessage(t('attributeSaved'));
      } else {
        const { isCopy, id } = attributesStore.getAttributeById(
          currentAttributeId.value
        )?.isCopied ?? { isCopy: false, id: null };
        if (isCopy) {
          await attributesStore.copyAttribute(
            attributeOverviewData,
            currentClientId.value,
            id
          );
          attributesStore.setTabIsCopyStatusToDefaultById(
            currentAttributeId.value
          );
          attributesStore.saveCreatedAttribute(
            currentAttributeId.value,
            attributesStore.attribute
          );
          displaySuccessMessage(t('attributeSaved'));
        } else {
          await attributesStore.createAttribute(
            currentClientId.value,
            attributeOverviewData
          );
          attributesStore.saveCreatedAttribute(
            currentAttributeId.value,
            attributesStore.attribute
          );
          displaySuccessMessage(t('attributeSaved'));
        }
      }
    } catch (error) {
      const errorResponse = error as AtbError;
      alertStore.showNotification(
        handleServerErrors(t, errorResponse?.errors[0].message),
        'Attributes',
        Motif.ERROR
      );
    }
  };

  const setDirtyField = (dirtyTabName: string, isDirty: boolean) => {
    attributesStore.setTabDirtyFieldById(
      currentAttributeId.value,
      dirtyTabName,
      isDirty
    );
  };

  const displaySuccessMessage = (message: string) => {
    alertStore.showNotification(message, 'Attributes', Motif.SUCCESS);
  };

  const saveCodeInVersionControls = async ({
    code: codeToSave,
  }: SaveAttributeCodeEvent) => {
    try {
      const overviewData = attributesStore.attributeDetailsMap.get(
        currentAttributeId.value
      ).overviewData;
      const body = {
        luaCode: codeToSave,
        isStagg: overviewData.isStagg,
        shortName: overviewData.shortName,
      };

      await attributesStore.checkCodeSyntax(currentClientId.value, body);
      attributesStore.updateCodeInVersionControlsById(
        Number(currentAttributeId.value),
        codeToSave
      );
      displaySaveModal.value = true;
      // TODO: Create an interface for this attribute or use an existing one
    } catch (error: any) {
      const message =
        error?.errors?.[0].error || 'Unknown error returned from server';
      alertStore.showNotification(message, 'Attributes', Motif.ERROR);
    }
  };

  const saveConfirmationModal = async () => {
    try {
      await attributesStore.saveAttributeCode(
        currentClientId.value,
        Number(currentAttributeId.value),
        {
          luaCode: loadData.value.code,
          comments: comment.value,
        }
      );
      setDirtyField(t('code'), false);
      displayConfirmationModal.value = false;
      alertStore.showNotification(
        t('attributeSaved'),
        'Attributes',
        Motif.SUCCESS
      );
      comment.value = '';
      displayConfirmationModal.value = false;
      displaySaveModal.value = false;
      // TODO: Create an interface for this attribute or use an existing one
    } catch (error: any) {
      displaySaveModal.value = false;
      displayConfirmationModal.value = false;
      const message =
        error?.errors?.[0].error || 'Unkown error returned from server';
      alertStore.showNotification(message, 'Attributes', Motif.ERROR);
    }
    try {
      await attributesStore.fetchAttributeById(
        currentClientId.value,
        Number(currentAttributeId.value)
      );
      attributesStore.setOverviewDataById(
        currentAttributeId.value,
        attributesStore.attribute
      );
    } catch (error) {
      console.error('error retrieving overview data');
    }
  };

  const closeSaveModal = () => {
    displaySaveModal.value = false;
    comment.value = '';
  };

  const closeConfirmationModal = () => {
    displayConfirmationModal.value = false;
  };

  const dynamicEventListner = () => {
    if (selectedTabIndex.value === 0) {
      return {
        codeMetaInformationsDeleted: deleteAttributeFromOverview,
        saveOverviewData: saveOverviewData,
        setDirtyStatus: setDirtyField,
      };
    } else if (selectedTabIndex.value === 1) {
      return {
        saveCode: saveCodeInVersionControls,
        setDirtyStatus: setDirtyField,
      };
    } else if (selectedTabIndex.value === 4) {
      return {
        createNewTab: createNewTab,
      };
    } else if (selectedTabIndex.value === 3) {
      return { rollbackCode: rollback };
    } else if (selectedTabIndex.value === 2) {
      return { validateCode: validateCode };
    }
  };

  const rollback = async (rollbackRevisionId: number) => {
    try {
      await attributesStore.rollbackAttributeCode(
        currentClientId.value,
        rollbackRevisionId
      );
      await attributesStore.fetchAttributeById(
        currentClientId.value,
        Number(currentAttributeId.value)
      );
      attributesStore.setOverviewDataById(
        currentAttributeId.value,
        attributesStore.attribute
      );
      alertStore.showNotification(
        t('successfulAttributeRollback'),
        'Attributes',
        Motif.SUCCESS
      );
      changeTab(FeatureTabs.CODE_TAB);
      // TODO: Create an interface for this attribute or use an existing one
    } catch (error: any) {
      alertStore.showNotification(
        t('rollbackError') + error.errors[0].message,
        'Attributes',
        Motif.ERROR
      );
    }
  };

  const getHistoryData = (
    currentPage: number,
    rowsPerPage: number,
    calculatePagination: (totalElements: number) => void
  ) => {
    return {
      getRows: async ({
        sortModel,
        successCallback,
        failCallback,
      }: IGetRowsParams) => {
        try {
          componentRef.value.gridApi.value?.showLoadingOverlay();
          await attributesStore.fetchHistoryDataById(
            currentClientId.value,
            Number(currentAttributeId.value),
            historyQueryParams(sortModel, {
              page: currentPage > 0 ? currentPage - 1 : 0,
              size: rowsPerPage,
            })
          );
          if (attributesStore.historyData!.numberOfElements > 0) {
            componentRef.value.gridApi.value?.hideOverlay();
          } else {
            componentRef.value.gridApi.value?.showNoRowsOverlay();
          }
          calculatePagination(attributesStore.historyData!.totalElements);
          successCallback(
            attributesStore.historyData!.content || [],
            attributesStore.historyData!.numberOfElements || 0
          );
        } catch (_error) {
          alertStore.showNotification(
            'Error fetching history list',
            '',
            Motif.ERROR
          );
          failCallback();
        }
      },
    };
  };

  const getWhereUsedData = (
    currentPage: number,
    rowsPerPage: number,
    calculatePagination: (totalElements: number) => void
  ) => {
    return {
      getRows: async ({ successCallback, failCallback }: IGetRowsParams) => {
        try {
          componentRef.value.gridApi.value?.showLoadingOverlay();
          await attributesStore.fetchWhereUsedDetails(
            currentClientId.value,
            Number(currentAttributeId.value)
          );
          if (attributesStore.whereUsedData.length === 0)
            return componentRef.value.gridApi.value?.showNoRowsOverlay();
          componentRef.value.gridApi.value?.hideOverlay();
          const offsetStart = (currentPage - 1) * rowsPerPage;
          const offsetEnd = currentPage * rowsPerPage;
          calculatePagination(attributesStore.whereUsedData.length);
          successCallback(
            attributesStore.whereUsedData.slice(offsetStart, offsetEnd),
            attributesStore.whereUsedData.length
          );
        } catch (_error) {
          alertStore.showNotification(
            'Error fetching where used list',
            '',
            Motif.ERROR
          );
          failCallback();
        }
      },
    };
  };

  const validateCode = async ({
    dataSourceName,
    file,
  }: ValidateFilterCodeEvent) => {
    try {
      await attributesStore.validateAttributeCode(
        currentClientId.value,
        Number(currentAttributeId.value),
        dataSourceName,
        file
      );
      const { ATTRIBUTE, FILTER, WARNING } = attributesStore.attributeValidate;
      componentRef.value.setArfs(attributesStore.attributeValidate.ARFS);

      if (WARNING) {
        alertStore.showNotification(WARNING, 'Warning', Motif.ERROR);
      }
      componentRef.value.transformData(ATTRIBUTE, CodeType.ATTRIBUTE);
      componentRef.value.transformData(FILTER, CodeType.FILTER);
      componentRef.value.toggleIsDataDisplayed(true);
    } catch (error) {
      const errorResponse = error as AtbError;
      componentRef.value.toggleIsDataDisplayed(false);
      alertStore.showNotification(
        errorResponse?.errors?.[0].error,
        'Warning',
        Motif.ERROR
      );
      console.error('error validating attribute code: ', error);
    }
  };

  const checkDuplicateShortName = async (shortName: string) => {
    try {
      await attributesStore.checkDuplicateShortName(
        currentClientId.value,
        shortName
      );
      return true;
    } catch (e: any) {
      alertStore.showNotification(
        e.errors[0].message,
        t('attributeAlertTitle'),
        Motif.ERROR
      );
      return false;
    }
  };
</script>
